import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  MeetingDetailDto,
  meetingDTO,
  MeetingRequest,
  SetMeetingFavoriteRequest,
  ZoomRequestDto,
  ZoomRespondDto,
  updateMeetingEntity,
} from "../../types/meeting";
import meetingApi from "../api/MeetingApi";

interface MeetingState {
  meetingDetail?: MeetingDetailDto;
  meetingDetailWithDiff?: MeetingDetailDto;
  meetingList?: meetingDTO[];
  meetingListInfo?: ZoomRespondDto;
}
// # initial state
const initialState: MeetingState = {
  meetingDetail: undefined,
  meetingList: [],
  meetingListInfo: undefined,
};

export const trunkGetMeetingList = createAsyncThunk(
  "meeting/list",
  async (req: { workgroupId: string; zoomDto: ZoomRequestDto }) => {
    const resp = await meetingApi.getMeetingList(req);
    return resp;
  }
);
export const trunkGetMeetingDetail = createAsyncThunk(
  "meeting/detail",
  async (req: {
    workgroupId: string;
    meetingId: string;
    occurrenceId?: string;
  }) => {
    const resp = await meetingApi.getMeetingDetail(req);
    return resp;
  }
);
export const trunkGetZoomDetailWithDiff = createAsyncThunk(
  "meeting/detailWithDiff",
  async (req: {
    workgroupId: string;
    meetingId: string;
    occurrenceId?: string;
  }) => {
    const resp = await meetingApi.getMeetingDetailWithDiff(req);
    return resp;
  }
);
export const trunkMeetingFavorite = createAsyncThunk(
  "meetings/favorite",
  async (reqDTO: SetMeetingFavoriteRequest) => {
    return await meetingApi.setMeetingFavorite(reqDTO);
  }
);
export const trunkGetMeetingListBySearchKeyword = createAsyncThunk(
  "meetings/list/search",
  async (reqDTO: MeetingRequest) => {
    return await meetingApi.getMeetingListBySearchKeyword(reqDTO);
  }
);
export const trunkCreateNewMeeting = createAsyncThunk(
  "meetings/new",
  async ({
    workgroupId,
    CreateOrUpdateZoomMeetingDTO,
  }: updateMeetingEntity) => {
    if (!CreateOrUpdateZoomMeetingDTO || !workgroupId) return;
    const resp = await meetingApi.createNewMeeting(
      workgroupId,
      CreateOrUpdateZoomMeetingDTO
    );
    return resp;
  }
);
export const trunkUpdateMeeting = createAsyncThunk(
  "meeting/update",
  async ({
    oneOrAllTF,
    workgroupId,
    meetingId,
    occurenceId,
    CreateOrUpdateZoomMeetingDTO,
  }: updateMeetingEntity) => {
    if (!meetingId || !CreateOrUpdateZoomMeetingDTO || !workgroupId) return;
    const resp = oneOrAllTF
      ? await meetingApi.updateMeeting(
          workgroupId,
          meetingId,
          "ALL",
          CreateOrUpdateZoomMeetingDTO
        )
      : await meetingApi.updateMeeting(
          workgroupId,
          meetingId,
          occurenceId ?? "NONE",
          CreateOrUpdateZoomMeetingDTO
        );

    return resp;
  }
);
export const trunkDeleteMeeting = createAsyncThunk(
  "meeting/delete",
  async ({
    oneOrAllTF,
    workgroupId,
    meetingId,
    occurenceId,
  }: updateMeetingEntity) => {
    if (!meetingId || !workgroupId) return;
    const resp: any = oneOrAllTF
      ? await meetingApi.deleteMeeting(workgroupId, meetingId, "ALL")
      : occurenceId &&
        (await meetingApi.deleteMeeting(workgroupId, meetingId, occurenceId));
    return resp;
  }
);
const meetingSlice = createSlice({
  name: "meeting",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(trunkGetMeetingList.fulfilled, (state, arg) => {
      if (arg.meta.arg.zoomDto.pageNo === 1) {
        state.meetingListInfo = arg.payload;
        state.meetingList = arg.payload.meetingInfoList;
      } else {
        if (state.meetingListInfo)
          state.meetingListInfo.meetingInfoList = [
            ...state.meetingListInfo.meetingInfoList,
            ...arg.payload.meetingInfoList,
          ];
        state.meetingList = [
          ...(state.meetingList ?? []),
          ...arg.payload.meetingInfoList,
        ];
      }
    });
    builder.addCase(
      trunkGetMeetingDetail.fulfilled,
      (state, { payload: data }) => {
        state.meetingDetail = data;
      }
    );
    builder.addCase(
      trunkGetMeetingDetail.rejected,
      (state, { payload: data }) => {
        state.meetingDetail = undefined;
      }
    );
    builder.addCase(
      trunkGetZoomDetailWithDiff.fulfilled,
      (state, { payload: data }) => {
        if (state.meetingDetail)
          state.meetingDetail.zoomMeetingDetailDTO = data.entity;
      }
    );
    builder.addCase(trunkMeetingFavorite.fulfilled, (state, req) => {
      if (req.meta.arg.page === "favorite") {
        state.meetingList = state.meetingList?.filter(
          (item) => item.meetingId + item.occurrenceId !== req.payload.meetingId
        );
      } else {
        state.meetingList = state.meetingList?.map((item) =>
          item.meetingId + item.occurrenceId === req.payload.meetingId
            ? { ...item, favorite: req.payload.flag ? "Y" : "N" }
            : item
        );
      }
    });
    builder.addCase(trunkDeleteMeeting.fulfilled, (state, arg) => {
      state.meetingList = state.meetingList?.filter(
        (item) =>
          !(
            item.meetingId === arg.meta.arg.meetingId &&
            (item.occurrenceId === arg.meta.arg.occurenceId ||
              arg.meta.arg.occurenceId === "ALL")
          )
      );
      if (state.meetingListInfo) {
        const deleteMeeting = state.meetingListInfo?.meetingInfoList?.filter(
          (item) =>
            item.meetingId !== arg.meta.arg.meetingId &&
            (item.occurrenceId !== arg.meta.arg.occurenceId ||
              arg.meta.arg.occurenceId !== "ALL")
        );
        state.meetingListInfo = {
          ...state.meetingListInfo,
          total: state.meetingListInfo.total - deleteMeeting?.length,
          meetingInfoList: state.meetingListInfo?.meetingInfoList?.filter(
            (item) =>
              !(
                item.meetingId === arg.meta.arg.meetingId &&
                (item.occurrenceId === arg.meta.arg.occurenceId ||
                  arg.meta.arg.occurenceId === "ALL")
              )
          ),
        };
      }
      // state.meetingFutureList = futureMeetings;
    });
    builder.addCase(
      trunkGetMeetingListBySearchKeyword.fulfilled,
      (state, resp) => {
        if (resp.meta.arg?.type === "root_search") return;
        if (resp.payload.pageNo === 1) {
          state.meetingListInfo = resp.payload;
          state.meetingList = resp.payload.meetingInfoList;
        } else {
          if (state.meetingListInfo)
            state.meetingListInfo.meetingInfoList = [
              ...state.meetingListInfo.meetingInfoList,
              ...resp.payload.meetingInfoList,
            ];
          state.meetingList = [
            ...(state.meetingList ?? []),
            ...resp.payload.meetingInfoList,
          ];
        }
      }
    );
  },
});
export const meetingAction = meetingSlice.actions;

export default meetingSlice.reducer;
